import React from 'react';
import {
  Table, TableBody, TableCell, TableHead, TableRow, Typography, Paper, Box,
} from '@material-ui/core';
import { WithStyles } from '@material-ui/core/styles';
import ArrowUpward from '@material-ui/icons/ArrowUpward';
import ArrowDownward from '@material-ui/icons/ArrowDownward';
import styles from './styles';
import { HubActivationMap } from '../../redux/types/hubs';
import { defaultDateTimeFormat, formatDate } from '../../utils/date';
import { Order } from '../../utils/table';

type SortField = 'serial_number' | 'created_by' | 'action' | 'created_on';

const sortFieldMap: Record<SortField, string> = {
  serial_number: 'hub_activity_log.serial_number',
  created_by: 'hub_activity_log.created_by',
  action: 'hub_activity_log.action',
  created_on: 'hub_activity_log.created_on',
};

const eventColors: Record<string, { bg: string; color: string }> = {
  activate: { bg: '#e6f9f0', color: '#1a9e6a' },
  mask_as_sold: { bg: '#ede7f6', color: '#7c3aed' },
  remove: { bg: '#fff8e1', color: '#f59e0b' },
};

const ActionMapping: Record<string, string> = {
  remove: 'Moved',
  activate: 'Activated',
  mask_as_sold: 'Sold',
};

const getInitials = (name: string) => name.split('.').map((part) => part[0]).join('').toUpperCase();

const ColGroup = () => (
  <colgroup>
    <col style={{ width: '25%' }} />
    <col style={{ width: '25%' }} />
    <col style={{ width: '25%' }} />
    <col style={{ width: '25%' }} />
  </colgroup>
);

type Props = {
  logs?: HubActivationMap[];
  orderBy: string;
  order: Order;
  onSort: (orderBy: string, order: Order) => void;
} & WithStyles<typeof styles>;

export const ActivationLogs = (props: Props) => {
  const {
    classes, logs, orderBy, order, onSort,
  } = props;

  const handleSort = (field: SortField) => {
    const backendField = sortFieldMap[field];
    if (orderBy === backendField) {
      onSort(backendField, order === 'asc' ? 'desc' : 'asc');
    } else {
      onSort(backendField, 'asc');
    }
  };

  const SortIcon = ({ field }: { field: SortField }) => {
    const backendField = sortFieldMap[field];
    if (orderBy !== backendField) return null;
    return order === 'asc'
      ? <ArrowUpward className={classes.sortIcon} />
      : <ArrowDownward className={classes.sortIcon} />;
  };

  return (
    <Paper className={classes.root}>
      <Box className={classes.header}>
        <Box className={classes.titleContainer}>
          <Typography component="span" className={classes.title}>Activation Logs</Typography>
          <span className={classes.badge}>{logs?.length}</span>
        </Box>
      </Box>

      <Table className={classes.table}>
        <ColGroup />
        <TableHead className={classes.tableHead}>
          <TableRow>
            <TableCell onClick={() => handleSort('serial_number')} className={classes.sortableCell}>
              <Box className={classes.sortCell}>
                Hub Serial
                <SortIcon field="serial_number" />
              </Box>
            </TableCell>
            <TableCell onClick={() => handleSort('created_by')} className={classes.sortableCell}>
              <Box className={classes.sortCell}>
                Activated By
                <SortIcon field="created_by" />
              </Box>
            </TableCell>
            <TableCell onClick={() => handleSort('action')} className={classes.sortableCell}>
              <Box className={classes.sortCell}>
                Event
                <SortIcon field="action" />
              </Box>
            </TableCell>
            <TableCell onClick={() => handleSort('created_on')} className={classes.sortableCell}>
              <Box className={classes.sortCell}>
                Created On
                <SortIcon field="created_on" />
              </Box>
            </TableCell>
          </TableRow>
        </TableHead>
      </Table>

      <Box className={classes.tableBody}>
        <Table className={classes.table}>
          <ColGroup />
          <TableBody>
            {logs?.map((log) => (
              <TableRow key={log.id} className={classes.tableRow}>
                <TableCell className={classes.hubSerial}>{log.serial_number}</TableCell>
                <TableCell>
                  <div className={classes.activatedByCell}>
                    <span className={classes.avatar}>{getInitials(log.created_by)}</span>
                    {log.created_by}
                  </div>
                </TableCell>
                <TableCell>
                  <span
                    className={classes.eventBadge}
                    style={{
                      background: eventColors[log?.action]?.bg,
                      color: eventColors[log?.action]?.color,
                    }}
                  >
                    {ActionMapping[log?.action]}
                  </span>
                </TableCell>
                <TableCell>{formatDate(log.created_on, defaultDateTimeFormat)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    </Paper>
  );
};
